import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, useTranslations } from 'react-ui';
import { useUserScope } from '../contexts/UserScope';
import { useBalloonEventsData } from '../contexts/EventData';

/**
 * Left-pane header shown when the map is in user-scoped mode (`/user/:slug`).
 * Renders a "back to all events" CTA, the user's avatar + name, and a
 * "View profile" link that is active when the profile pane is closed and
 * greyed-out (non-interactive) when it is already open.
 */
export const UserScopeHeader: React.FC = () => {
    const { slug, profile, paneOpen, openProfilePane } = useUserScope();
    const { deselectEvent } = useBalloonEventsData();
    const { translated } = useTranslations();
    const navigate = useNavigate();

    if (!slug) return null;

    const name = profile?.name ?? slug;

    // Opening the profile dismisses any open event so it doesn't linger behind.
    const handleViewProfile = () => {
        deselectEvent();
        openProfilePane();
    };

    return (
        <div className="user-scope-header px-4 pt-3 pb-3 border-b border-gray-100">
            <button
                type="button"
                onClick={() => navigate('/')}
                className="text-sm text-gray-500 hover:text-gray-800 transition-colors flex items-center gap-1 mb-3"
            >
                <span aria-hidden>‹</span> {translated('all_events') || 'All events'}
            </button>

            <div className="flex items-center gap-3">
                <UserAvatar
                    name={name}
                    avatar={profile?.avatar}
                    media={profile?.media}
                    size={40}
                    className="flex-shrink-0"
                />

                <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 truncate">{name}</p>
                    {paneOpen ? (
                        <span className="text-xs text-gray-400">{translated('viewing_profile') || 'Viewing profile'}</span>
                    ) : (
                        <button
                            type="button"
                            onClick={handleViewProfile}
                            className="text-xs text-balloon hover:underline text-left"
                        >
                            {translated('view_profile') || 'View profile'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserScopeHeader;
